import * as THREE from 'three';

// Keep the shield facing the camera, independently of the die's resting angle.
export const createShieldScene = (scene, surfaceY) => {
  const field = new THREE.Group();
  const crest = new THREE.Group();
  scene.add(field, crest);
  field.visible = crest.visible = false;

  const shellMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { strength: { value: 0 } },
    vertexShader: `
      varying vec3 worldNormal;
      varying vec3 worldPosition;
      void main() {
        worldNormal = normalize(mat3(modelMatrix) * normal);
        vec4 positionWorld = modelMatrix * vec4(position, 1.0);
        worldPosition = positionWorld.xyz;
        gl_Position = projectionMatrix * viewMatrix * positionWorld;
      }
    `,
    fragmentShader: `
      uniform float strength;
      varying vec3 worldNormal;
      varying vec3 worldPosition;
      void main() {
        vec3 viewDirection = normalize(cameraPosition - worldPosition);
        float rim = pow(1.0 - max(dot(normalize(worldNormal), viewDirection), 0.0), 2.5);
        gl_FragColor = vec4(mix(vec3(0.12, 0.65, 0.9), vec3(0.7, 1.0, 1.0), rim),
          (0.015 + rim * 0.34) * strength);
      }
    `,
  });
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(0.94, 32, 20),
    shellMaterial
  );
  field.add(shell);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.13);
  shape.quadraticCurveTo(0.42, 0.92, 0.98, 0.83);
  shape.lineTo(0.91, -0.03);
  shape.quadraticCurveTo(0.8, -0.72, 0, -1.12);
  shape.quadraticCurveTo(-0.8, -0.72, -0.91, -0.03);
  shape.lineTo(-0.98, 0.83);
  shape.quadraticCurveTo(-0.42, 0.92, 0, 1.13);

  const paneMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    uniforms: {
      elapsed: { value: 0 },
      motion: { value: 1 },
      strength: { value: 0 },
    },
    vertexShader: `
      varying vec2 shieldPosition;
      void main() {
        shieldPosition = position.xy;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float elapsed;
      uniform float motion;
      uniform float strength;
      varying vec2 shieldPosition;
      void main() {
        float sweep = mod(elapsed * 1.65, 4.8) - 2.2;
        float shine = (1.0 - smoothstep(0.025, 0.19,
          abs(shieldPosition.x + shieldPosition.y * 0.5 - sweep))) * motion;
        float edge = smoothstep(0.3, 1.0, abs(shieldPosition.x));
        vec3 color = mix(vec3(0.15, 0.75, 1.0), vec3(0.9, 1.0, 1.0), shine);
        gl_FragColor = vec4(color, (0.035 + edge * 0.07 + shine * 0.24) * strength);
      }
    `,
  });
  crest.add(new THREE.Mesh(new THREE.ShapeGeometry(shape, 24), paneMaterial));

  const outline = new THREE.CatmullRomCurve3(
    shape
      .getPoints(20)
      .slice(0, -1)
      .map(({ x, y }) => new THREE.Vector3(x, y, 0)),
    true,
    'centripetal'
  );
  const rimMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xd8ffff,
    emissive: 0x5ddff8,
    emissiveIntensity: 0.6,
    metalness: 0.78,
    roughness: 0.12,
    clearcoat: 1,
    transparent: true,
    depthWrite: false,
  });
  const rim = new THREE.Mesh(
    new THREE.TubeGeometry(outline, 100, 0.018, 6, true),
    rimMaterial
  );
  crest.add(rim);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0x62eaff,
    transparent: true,
    opacity: 0.16,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  crest.add(
    new THREE.Mesh(
      new THREE.TubeGeometry(outline, 100, 0.055, 6, true),
      glowMaterial
    )
  );
  const innerRim = new THREE.Mesh(rim.geometry, glowMaterial);
  innerRim.scale.setScalar(0.91);
  crest.add(innerRim);

  const sparkMaterial = new THREE.MeshBasicMaterial({
    color: 0xe4ffff,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const sparkGeometry = new THREE.OctahedronGeometry(0.055);
  const sparks = Array.from({ length: 8 }, () => {
    const spark = new THREE.Mesh(sparkGeometry, sparkMaterial);
    crest.add(spark);
    return spark;
  });
  const waveMaterial = glowMaterial.clone();
  const wave = new THREE.Mesh(
    new THREE.RingGeometry(0.73, 0.77, 64),
    waveMaterial
  );
  wave.rotation.x = -Math.PI / 2;
  wave.visible = false;
  scene.add(wave);
  const cameraOffset = new THREE.Vector3(0, 0, 0.92);
  let elapsed = 0;
  let active = false;

  return {
    activate() {
      active = true;
      elapsed = 0;
    },
    reset() {
      active = false;
      field.visible = crest.visible = wave.visible = false;
    },
    update({ diePosition, camera, delta, reduceMotion, visible }) {
      field.visible = crest.visible = active && visible;
      wave.visible = active && visible && !reduceMotion && elapsed < 1.25;
      if (!active || !visible) return;
      elapsed += delta;
      const progress = reduceMotion ? 1 : Math.min(1, elapsed / 0.6);
      const reveal = 1 - Math.pow(1 - progress, 3);
      field.position.copy(diePosition);
      field.scale.setScalar(0.3 + reveal * 0.7);
      cameraOffset.set(0, 0, 0.92).applyQuaternion(camera.quaternion);
      crest.position.copy(diePosition).add(cameraOffset);
      crest.quaternion.copy(camera.quaternion);
      crest.scale.setScalar(0.3 + reveal * 0.7);
      shellMaterial.uniforms.strength.value = reveal;
      paneMaterial.uniforms.strength.value = reveal;
      paneMaterial.uniforms.elapsed.value = elapsed;
      paneMaterial.uniforms.motion.value = reduceMotion ? 0 : 1;
      rimMaterial.opacity = reveal * 0.9;
      glowMaterial.opacity = reveal * 0.2;
      sparks.forEach((spark, index) => {
        spark.visible = !reduceMotion && elapsed < 1.8;
        if (!spark.visible) return;
        const point = outline.getPointAt(
          (index / sparks.length + elapsed * 0.15) % 1
        );
        spark.position.copy(point).multiplyScalar(1.08 + elapsed * 0.06);
        spark.rotation.z = elapsed * 2 + index;
        spark.scale.setScalar(0.5 + Math.sin(elapsed * 5 + index) ** 2);
      });
      sparkMaterial.opacity = Math.max(0, 1 - elapsed / 1.8);
      wave.position.set(diePosition.x, surfaceY + 0.06, diePosition.z);
      wave.scale.setScalar(0.5 + elapsed * 1.3);
      waveMaterial.opacity = Math.max(0, 0.65 * (1 - elapsed / 1.25));
    },
    dispose() {
      const geometries = new Set();
      const materials = new Set();
      [field, crest, wave].forEach((group) => {
        group.traverse((object) => {
          if (object.geometry) geometries.add(object.geometry);
          if (object.material) materials.add(object.material);
        });
        scene.remove(group);
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
    },
  };
};
